import type { BeatEvent, BeatMap } from '../schema/v1'
import { roundBeats } from './beatmap'

/**
 * Grabar cambios (§5.4): marcas tomadas con un toque, la barra espaciadora o el pedal. Cada marca
 * es el pulso en que empieza el acorde siguiente, contado desde el inicio de la toma. Se guarda la
 * duración tal como se marcó (`tapped`) y la propuesta en pulsos; ajustar a la rejilla no borra
 * lo marcado.
 */

/** Rejillas para ajustar las marcas: un pulso, medio pulso o sin ajustar. */
export const GRIDS = [1, 0.5] as const
export type Grid = (typeof GRIDS)[number] | null

const MIN_TAPPED = 1 / 16
const MAX_BEATS = 256

/** Duraciones a partir de las marcas de una toma. Las marcas que no avanzan se descartan. */
export function takeDurations(marks: readonly number[]): number[] {
  const durations: number[] = []
  let previous = 0
  for (const mark of marks) {
    if (!(mark > previous + MIN_TAPPED / 2)) continue
    durations.push(roundBeats(Math.min(MAX_BEATS, mark - previous)))
    previous = mark
  }
  return durations
}

/**
 * Ajusta duraciones a la rejilla redondeando las posiciones acumuladas, no cada duración por
 * separado: así el error no se acumula a lo largo de la toma. Ninguna queda por debajo de la
 * rejilla.
 */
export function quantize(raw: readonly number[], grid: number): number[] {
  const result: number[] = []
  let exact = 0
  let placed = 0
  for (const duration of raw) {
    exact += duration
    const target = Math.max(Math.round(exact / grid) * grid, placed + grid)
    result.push(roundBeats(Math.min(MAX_BEATS, target - placed)))
    placed = target
  }
  return result
}

/**
 * Aplica una toma al mapa: el evento `from` y los siguientes reciben las duraciones marcadas, y el
 * resto del mapa queda igual. El mapa tiene que estar completo (un evento por acorde).
 */
export function applyTake(
  beatMap: BeatMap,
  from: number,
  marks: readonly number[],
  grid: Grid,
): BeatMap {
  const durations = takeDurations(marks)
  const count = Math.min(durations.length, beatMap.events.length - from)
  if (from < 0 || count <= 0) return beatMap
  const adjusted = grid === null ? durations : quantize(durations, grid)
  const events = beatMap.events.map((event, index): BeatEvent => {
    const offset = index - from
    if (offset < 0 || offset >= count) return event
    return { ...event, beats: Math.max(adjusted[offset]!, MIN_TAPPED), tapped: durations[offset]! }
  })
  return { ...beatMap, events }
}

/**
 * Vuelve a ajustar lo marcado con otra rejilla (o sin ajustar). Cada tramo seguido de eventos
 * marcados se ajusta junto, para que el redondeo no se acumule.
 */
export function requantize(beatMap: BeatMap, grid: Grid): BeatMap {
  const events = [...beatMap.events]
  let index = 0
  while (index < events.length) {
    if (events[index]!.tapped === undefined) {
      index += 1
      continue
    }
    let end = index
    while (end < events.length && events[end]!.tapped !== undefined) end += 1
    const raw = events.slice(index, end).map((event) => event.tapped!)
    const adjusted = grid === null ? raw : quantize(raw, grid)
    for (let offset = 0; offset < raw.length; offset += 1) {
      events[index + offset] = { ...events[index + offset]!, beats: adjusted[offset]! }
    }
    index = end
  }
  return { ...beatMap, events }
}

/**
 * Cambia a mano la duración de un evento. Lo que se toca a mano deja de ser una marca: un ajuste
 * posterior a la rejilla no lo pisa.
 */
export function setEventBeats(beatMap: BeatMap, index: number, beats: number): BeatMap {
  const event = beatMap.events[index]
  if (!event) return beatMap
  const value = roundBeats(Math.min(MAX_BEATS, Math.max(MIN_TAPPED, beats)))
  if (value === event.beats && event.tapped === undefined) return beatMap
  const next: BeatEvent = { ...event, beats: value }
  delete next.tapped
  const events = [...beatMap.events]
  events[index] = next
  return { ...beatMap, events }
}

/** ¿Hay algo marcado que se pueda volver a ajustar? */
export function hasTapped(beatMap: BeatMap): boolean {
  return beatMap.events.some((event) => event.tapped !== undefined)
}
